import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { applyLangToDocument, LANG_META, SUPPORTED_LANGS, type Lang } from "../i18n";

/**
 * Compact language switcher for the Header. Shows the short code of the
 * active language (RU / EN / HE) and opens a tiny popover with full names
 * when tapped. Saves the choice to localStorage via i18next-language-detector
 * and flips document direction (RTL for Hebrew, LTR otherwise).
 */
export function LanguageToggle() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const current = (SUPPORTED_LANGS as readonly string[]).includes(i18n.language)
    ? (i18n.language as Lang)
    : "ru";
  const currentMeta = LANG_META[current];

  function choose(lang: Lang) {
    i18n.changeLanguage(lang);
    applyLangToDocument(lang);
    setOpen(false);
  }

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("lang.label")}
        aria-expanded={open}
        className="inline-flex items-center gap-1 px-2.5 h-8 rounded-full text-[12px] font-bold tracking-wider text-ink-700 hover:bg-ink-50 transition-colors"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20" />
          <path d="M12 2a15.3 15.3 0 0 1 0 20" />
          <path d="M12 2a15.3 15.3 0 0 0 0 20" />
        </svg>
        <span>{currentMeta.short}</span>
      </button>

      {open && (
        // end-0 + RTL-aware: in LTR the popover hugs the right edge of the
        // toggle, in RTL the left edge. `right-0` previously caused it to
        // overflow past the screen edge in Hebrew.
        <div className="absolute end-0 mt-1 min-w-[130px] py-1 rounded-xl bg-surface border border-ink-100 shadow-lg z-50">
          {SUPPORTED_LANGS.map((lang) => {
            const meta = LANG_META[lang];
            const active = lang === current;
            return (
              <button
                key={lang}
                type="button"
                onClick={() => choose(lang)}
                className={`w-full flex items-center justify-between px-3 py-2 text-[13px] hover:bg-ink-50 transition-colors ${
                  active ? "font-bold text-brand-purple" : "text-ink-900"
                }`}
              >
                <span>{meta.native}</span>
                <span className="text-[11px] text-ink-400 font-mono">{meta.short}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
